import { ChangeDetectionStrategy, Component, OnInit, inject, input } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatAutocomplete, MatAutocompleteTrigger, MatOption } from '@angular/material/autocomplete';
import type { MatOptionSelectionChange } from '@angular/material/core';
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { InputErrorsComponent } from '@comparit/core';
import { AddressService } from '../../services/address.service';
import type { StrasseSuggestionDto } from '@comparit/common-api';
import { Observable } from 'rxjs';

@Component({
  selector: 'ui-address-street-control',
  imports: [
    AsyncPipe,
    ReactiveFormsModule,
    MatAutocompleteTrigger,
    MatAutocomplete,
    MatOption,
    MatFormField,
    MatLabel,
    MatInput,
    MatError,
    MatProgressSpinner,
    InputErrorsComponent,
  ],
  templateUrl: './address-street-control.component.html',
  styleUrl: './address-street-control.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddressStreetControlComponent implements OnInit {
  streetControl = input.required<FormControl<string | null>>();
  /** Passed in to narrow the search, and corrected from the chosen suggestion. */
  zipControl = input<FormControl<string | null>>();
  cityControl = input<FormControl<string | null>>();
  numberControl = input<FormControl<string | null>>();

  private addressService = inject(AddressService);

  protected streetResult$!: Observable<StrasseSuggestionDto[] | undefined>;
  protected isLoading = this.addressService.selectIsLoadingStreet();

  ngOnInit() {
    this.streetResult$ = this.addressService.selectAddressByStreetChanges(
      this.streetControl(),
      this.zipControl(),
      this.cityControl()
    );
  }

  /**
   * The option's value already writes the street. The suggestion carries its own PLZ/Ort, so
   * they are corrected to match, and the house number is cleared because it belonged to the
   * street that was just replaced.
   */
  protected onStreetSelect(item: StrasseSuggestionDto, event: MatOptionSelectionChange) {
    // Material also fires this for the option being deselected; acting on it would write the
    // previous option's PLZ/Ort and wipe the house number.
    if (!event.isUserInput) return;

    setIfChanged(this.zipControl(), item.plz);
    setIfChanged(this.cityControl(), item.ort);

    this.numberControl()?.setValue('');
  }
}

function setIfChanged(control: FormControl<string | null> | undefined, value?: string) {
  if (!control || !value) return;
  if (control.getRawValue() === value) return;
  control.setValue(value);
}
